import { useLayoutEffect, useRef, useState } from 'react';
import {
  getIndicatorBarLabelLayout,
  type IndicatorBarGeometry,
  type IndicatorBarLabelLayout,
} from './investmentIndicatorBarLayout';

/** Measure natural text independently of the wrapping/placement we apply to it. */
export const useInvestmentIndicatorBarLayout = (
  geometry: IndicatorBarGeometry | null,
  display: string,
) => {
  const plotRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const [layout, setLayout] = useState<IndicatorBarLabelLayout | null>(null);

  useLayoutEffect(() => {
    const plot = plotRef.current;
    const label = labelRef.current;
    if (!plot || !label) return undefined;
    let disposed = false;
    let frameId: number | null = null;
    const requestFrame = window.requestAnimationFrame ?? (
      (callback: FrameRequestCallback) => window.setTimeout(() => callback(0), 0)
    );
    const cancelFrame = window.cancelAnimationFrame ?? window.clearTimeout;

    const measure = () => {
      const width = plot.getBoundingClientRect().width;
      if (width <= 0) return; // A hidden container must be measured when shown.
      const probe = label.cloneNode(true) as HTMLSpanElement;
      probe.removeAttribute('id');
      probe.setAttribute('aria-hidden', 'true');
      probe.setAttribute('data-indicator-label-probe', '');
      Object.assign(probe.style, {
        position: 'absolute', left: '0', right: 'auto', top: '0',
        width: 'max-content', minWidth: '0', maxWidth: 'none', height: 'auto',
        display: 'block', whiteSpace: 'nowrap', transform: 'none',
        visibility: 'hidden', pointerEvents: 'none',
      });
      // Insert beside the original so inherited font and label padding match.
      label.parentElement?.appendChild(probe);
      try {
        const next = getIndicatorBarLabelLayout(geometry, width, probe.getBoundingClientRect().width);
        if (next) setLayout(current => (
          current?.mode === next.mode && current.left === next.left ? current : next
        ));
      } finally {
        probe.remove();
      }
    };
    const schedule = () => {
      if (disposed || frameId !== null) return;
      frameId = requestFrame(() => {
        frameId = null;
        if (!disposed) measure();
      });
    };

    measure();
    const widths = new WeakMap<Element, number>();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(entries => {
      let changed = false;
      entries.forEach(entry => {
        if (widths.get(entry.target) !== entry.contentRect.width) {
          widths.set(entry.target, entry.contentRect.width);
          changed = true;
        }
      });
      if (changed) schedule();
    });
    observer?.observe(plot);
    observer?.observe(label);
    window.addEventListener('resize', schedule);
    const fonts = document.fonts;
    fonts?.addEventListener?.('loadingdone', schedule);
    if (fonts?.ready) void fonts.ready.then(schedule, () => undefined);

    return () => {
      disposed = true;
      if (frameId !== null) cancelFrame(frameId);
      observer?.disconnect();
      window.removeEventListener('resize', schedule);
      fonts?.removeEventListener?.('loadingdone', schedule);
    };
  }, [geometry, display]);

  return { plotRef, labelRef, layout };
};
